import { useCallback } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useStateManager } from './useStateManager';
import { clearStoredInterestData } from '../utils/interestDataUtils';
import { initializeSession, clearSession } from '../utils/sessionManager';
import { DealerManager } from '../utils/dealerManager';

interface UseChatCleanupProps {
  dealerId: string;
  clearMessages: () => void;
  closeForm: () => void;
  setShowNewConversationDialog: (show: boolean) => void;
  setInput: (value: string) => void;
  abortCurrentRequest: () => void;
  clearConversation: () => void; // OpenAI Responses API conversation cleanup
}

export const useChatCleanup = ({
  dealerId,
  clearMessages,
  closeForm,
  setShowNewConversationDialog,
  setInput,
  abortCurrentRequest,
  clearConversation
}: UseChatCleanupProps) => {
  const { t } = useLanguage();
  const { clearState } = useStateManager();

  const handleConfirmNewConversation = useCallback(async () => {
    // 1. Clear chat messages and input
    clearMessages();
    setInput('');
    setShowNewConversationDialog(false);
    
    // 2. Close any open forms
    closeForm();
    
    // 3. Abort any pending OpenAI requests and clear conversation
    abortCurrentRequest();
    clearConversation(); // Clear OpenAI Responses API conversation history and previous_response_id
    
    // Import needed for database cleanup
    const { securedSupabase } = await import('@/integrations/supabase/secured-client');
    const { sessionManager } = await import('@/utils/sessionManager');
    
    // 4. Clear vehicle interests from database for this session
    try {
      const session = sessionManager.getCurrentSession();
      const sessionId = session?.sessionId;
      if (sessionId && dealerId && !isNaN(parseInt(dealerId))) {
        // Use base supabase client to avoid dealer context headers interfering with edge functions
        const { supabase } = await import('@/integrations/supabase/client');
        
        await supabase.functions.invoke('vehicle-interest', {
          body: {
            action: 'delete',
            session_id: sessionId,
            dealer_id: parseInt(dealerId)
          }
        });
      }
    } catch (error) {
      // Silent error handling
    }

    // 5. Clear ALL localStorage (complete wipe)
    try {
      // Clear all chat state using state manager
      await clearState();
      clearSession();
      
      // Clear interest data
      clearStoredInterestData();
      
      // Clear remaining localStorage items
      const itemsToRemove = [
        'lastFormCloseTime',
        'carfax_requests',
        'navigation_history',
        'recent_response',
        'recent_sent_message',
        'Website_Current_Vehicle',
        'Price_Disclaimer',
        'Site_Section',
        'dealerData',
        'dealerDataTimestamp',
        'chatbot_session',
        'chatbot_interest_data',
        'vehicleInterestData',
        'context-current-page'
        // Note: 'collapsed-icon-dismissed' is NOT cleared - it persists across conversations
      ];
      
      itemsToRemove.forEach(item => localStorage.removeItem(item));
    } catch (error) {
      // Silent error handling
    }
    
    // 6. Clear dealer cache and force fresh lookup
    DealerManager.clearCache();
    
    // 7. Wait for dealer re-authentication before proceeding
    try {
      await DealerManager.initializeFromGlobalSettings();
    } catch (error) {
      // Silent error handling
    }
    
    // 8. Initialize new session immediately to ensure clean chat transcript
    try {
      initializeSession();
    } catch (error) {
      // Silent error handling
    }
    
    // 9. Refresh prompt menu to fetch fresh data (now that dealer is re-authenticated)
    try {
      if ((window as any).refreshPromptMenu) {
        (window as any).refreshPromptMenu();
      }
    } catch (error) {
      // Silent error handling
    }
    
    // 10. Emit event to reset vehicle interest dropdown
    window.dispatchEvent(new CustomEvent('newConversationStarted'));
    
    // 11. Show success toast
    toast.success(t('toast.new.conversation.started'));
    
    // 12. Force full page reload from server to fetch fresh app version
    setTimeout(() => {
      window.location.reload();
    }, 500); // Small delay to allow toast to show
  }, [
    dealerId,
    clearMessages, 
    closeForm, 
    setShowNewConversationDialog, 
    setInput, 
    abortCurrentRequest, 
    clearConversation,
    t
  ]);

  return {
    handleConfirmNewConversation,
  };
};